import { merge } from '../../../helper';
import * as exElement from '../../../utils/element';

import alreadyTableBody from '../utils/alreadyTableBody';
import { refresh } from '../refresh';
import type { TypeTableRowsDataConfig } from '../types';

// ----------------------------------------------------------------------------

type TypeDataCellCallback = {
    columnIndex: number;
    element: HTMLTableCellElement;
    row: HTMLTableRowElement;
    rowIndex?: number;
    source: any[] | Record<string, any>;
    value: any;
};

// ----------------------------------------------------------------------------

function data(element: HTMLElement, config: TypeTableRowsDataConfig): any[] {
    if (!(element instanceof HTMLTableElement)) {
        console.error('Is not an element table...');
        return [];
    }

    const defConfig: Record<string, any> = {
        data: [],
        tbody: {},
    };

    merge(defConfig, config);

    const elTableBody = alreadyTableBody(element, defConfig.tbody);
    const rows: any[] = [];

    for (let r = 0; r < defConfig.data.length; r++) {
        const itemRow = defConfig.data[r];

        const elRow = tableBodyRow(elTableBody, r, defConfig);

        if (r % 2 == 0) {
            elRow.addClass('odd');
            elRow.removeClass('even');
        } else {
            elRow.addClass('even');
            elRow.removeClass('odd');
        }

        if (typeof defConfig.fnRow === 'function') {
            defConfig.fnRow({
                element: elRow,
                rowIndex: r,
            });
        }

        const rowConfig = {
            ...defConfig,
            rowIndex: r,
        };
        let columns: HTMLTableCellElement[] = [];

        if (Array.isArray(itemRow)) {
            columns = dataArray(elRow, itemRow, rowConfig);
        } else if (itemRow?.constructor === Object) {
            columns = dataObject(elRow, itemRow, rowConfig);
        }

        rows.push({
            row: elRow,
            columns: columns,
        });
    }

    refresh(element, config);

    return rows;
}

function tableBodyRow(
    elTableBody: HTMLTableSectionElement,
    rowIndex: number,
    config: Record<string, any> = {},
): HTMLTableRowElement {
    let elRow = elTableBody.rows[rowIndex];

    if (!elRow || config.already !== true) {
        elRow = exElement.create({
            merge: config.tbody.row,
            tag: 'tr',
        }) as HTMLTableRowElement;
        elTableBody.appendChild(elRow);
    }

    return elRow;
}

function tableBodyCell(
    elRow: HTMLTableRowElement,
    columnIndex: number,
    config: Record<string, any> = {},
): HTMLTableCellElement {
    let elColumn = elRow.cells[columnIndex];

    if (!elColumn || config.already !== true) {
        elColumn = exElement.create({
            merge: config.tbody.cell,
            tag: 'td',
        }) as HTMLTableCellElement;
        elRow.appendChild(elColumn);
    }

    return elColumn;
}

function dataArray(
    elRow: HTMLTableRowElement,
    itemData: any[],
    config: Record<string, any> = {},
): HTMLTableCellElement[] {
    return dataEach(
        elRow,
        itemData,
        config.columnCount ?? itemData.length,
        function (columnIndex): any {
            return itemData[columnIndex];
        },
        config,
    );
}

function dataObject(
    elRow: HTMLTableRowElement,
    itemData: Record<string, any>,
    config: Record<string, any> = {},
): HTMLTableCellElement[] {
    const keys = Object.keys(itemData);

    return dataEach(
        elRow,
        itemData,
        config.columnCount ?? keys.length,
        function (columnIndex): any {
            return itemData[keys[columnIndex]];
        },
        config,
    );
}

function dataEach(
    elRow: HTMLTableRowElement,
    itemData: any[] | Record<string, any>,
    columnCount: number,
    getValue: (columnIndex: number) => any,
    config: Record<string, any> = {},
): HTMLTableCellElement[] {
    const columns: HTMLTableCellElement[] = [];

    for (let c = 0; c < columnCount; c++) {
        const elColumn = dataCell(elRow, getValue(c), c, itemData, config);

        columns.push(elColumn);
    }

    return columns;
}

function dataCell(
    elRow: HTMLTableRowElement,
    value: any,
    columnIndex: number,
    source: any[] | Record<string, any>,
    config: Record<string, any> = {},
): HTMLTableCellElement {
    const elColumn = tableBodyCell(elRow, columnIndex, config);

    if (typeof config.fnCell === 'function') {
        config.fnCell({
            element: elColumn,
            row: elRow,
            rowIndex: config.rowIndex,
            columnIndex: columnIndex,
            source: source,
            value: value,
        } satisfies TypeDataCellCallback);
    } else if (value != null) {
        elColumn.child(value);
    }

    if (typeof config.fnColumn === 'function') {
        config.fnColumn({
            element: elColumn,
            rowIndex: config.rowIndex,
            columnIndex: columnIndex,
        });
    }

    return elColumn;
}

// ----------------------------------------------------------------------------

export { data, dataArray, dataCell, dataObject, tableBodyCell, tableBodyRow };
